import { motion, useAnimate } from 'motion/react';
import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IKeyStatsCard, ISideNavFooterKeyStatDiscrepancy } from 'views/ProFormaTable/types';
import { useAnimatedNumber } from 'views/ProFormaTable/hooks/useAnimatedNumber';
import { NSCard } from 'bricks';

interface ProFormaFooterKeyStatCardProps {
    index: number;
    stat: IKeyStatsCard;
    keyStatsActualsDiscrepancies: Record<string, ISideNavFooterKeyStatDiscrepancy>;
    shouldAnimate: boolean;
}

export const ProFormaFooterKeyStatCard = ({
    stat, index, keyStatsActualsDiscrepancies, shouldAnimate,
}: ProFormaFooterKeyStatCardProps) => {
    const textRef = useRef<HTMLDivElement>(null);

    const [scope, animateDom] = useAnimate();

    const { value } = stat;

    const previousValueRef = useRef(value);
    const previousValue = previousValueRef.current;
    const animatedValue = useAnimatedNumber(value);

    useEffect(() => {
        const run = async () => {
            if (!shouldAnimate) return;

            const formattedValue = stat.formatter(value);
            const formattedPreviousValue = stat.formatter(previousValue);

            if (formattedValue === formattedPreviousValue) return;

            const glowColor = '15,179,255';
            const glowNone = `0 0 0 0 rgba(${glowColor},0)`;
            const glowTransition = [glowNone, `0 0 10px 4px rgba(${glowColor},0.50)`, glowNone];

            const animations = [];

            if (scope.current) {
                animateDom(
                    scope.current,
                    { scale: [1, 1.03, 1], boxShadow: glowTransition },
                    { duration: 0.9, ease: 'easeInOut' },
                );
            }

            if (textRef.current) {
                animations.push(
                    animateDom(
                        textRef.current,
                        { scale: [1, 1.08, 1], transformOrigin: 'left' },
                        { duration: 0.9, ease: 'easeInOut' },
                    ),
                );
            }

            await Promise.all(animations);

            const shimmerEl = scope.current?.querySelector('.shimmer');
            if (shimmerEl) {
                await animateDom(
                    shimmerEl,
                    { x: ['-120%', '120%'], opacity: [0, 0.6, 0] },
                    { duration: 0.8, ease: 'easeInOut' },
                );
            }
        };

        run();
    }, [value, shouldAnimate, stat.formatter]);

    useEffect(() => {
        previousValueRef.current = value;
    }, [value]);

    return (
        <NSCard
            ref={scope}
            key={`${stat.title}-${index}`}
            className={`p-2 w-100 text-muted position-relative overflow-hidden font-13
                ${stat.marginClass} ${index === 0 ? 'mr-1' : ''} 
                ProFormaSideNav__footer__metric-card ProFormaSideNav__footer__metric-card${keyStatsActualsDiscrepancies[stat.title]?.cardColor}`}
        >
            <motion.div
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                    delay: index * 0.1, type: 'spring', stiffness: 140, damping: 18,
                }}
            >
                <div
                    className="shimmer pointer-events-none position-absolute"
                    style={{
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        transform: 'translateX(-120%) skewX(-12deg)',
                        background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.1), transparent)',
                        pointerEvents: 'none',
                    }}
                />

                <FontAwesomeIcon
                    icon={stat.icon}
                    className={`ProFormaSideNav__footer__metric-card__bg-icon ${keyStatsActualsDiscrepancies[stat.title]?.iconColor}`}
                />
                <div>{stat.title}</div>
                <div ref={textRef} className="text-dark font-weight-bold">{stat.formatter(shouldAnimate ? animatedValue : value)}</div>
            </motion.div>
        </NSCard>
    );
};
